import type { ReactNode } from 'react';
import { Box, ButtonGroup, HStack, Icon, Text } from '@chakra-ui/react';
import { Info, TriangleAlert } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { testIds } from './testIds';

function NeedsAttentionRow({
  tone,
  title,
  description,
  actions,
}: {
  tone: 'warning' | 'info';
  title: ReactNode;
  description: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <HStack
      align="center"
      justify="space-between"
      spacing={4}
      px={4}
      py={3}
      borderTopWidth="1px"
      borderColor="orange.100"
      _first={{ borderTopWidth: 0 }}
    >
      <HStack align="center" spacing={2.5} minW={0}>
        <Icon
          as={tone === 'warning' ? TriangleAlert : Info}
          boxSize={iconSize.sm}
          color={tone === 'warning' ? 'orange.500' : 'fg.subtle'}
          flexShrink={0}
        />
        <Text fontSize="sm">
          <Text as="span" fontWeight="semibold">
            {title}
          </Text>{' '}
          <Text as="span" color="fg.muted">
            {description}
          </Text>
        </Text>
      </HStack>
      {actions === undefined ? null : (
        <ButtonGroup size="sm" spacing={2} flexShrink={0}>
          {actions}
        </ButtonGroup>
      )}
    </HStack>
  );
}

// The single consolidated action zone: one row per live issue, sentence on the
// left, actions in a clean right-hand column. Renders nothing when there is
// nothing to do, so the page stays quiet when healthy.
export function NeedsAttention({ children }: { children: ReactNode }) {
  return (
    <Box>
      <HStack spacing={2} mb={2}>
        <Icon as={TriangleAlert} boxSize={iconSize.sm} color="orange.500" />
        <Text fontSize="md" fontWeight="bold">
          <Trans>Needs attention</Trans>
        </Text>
      </HStack>
      <Box
        borderWidth="1px"
        borderColor="orange.200"
        borderRadius="md"
        bg="bg.surface"
        overflow="hidden"
        data-testid={testIds.banner}
      >
        {children}
      </Box>
    </Box>
  );
}

NeedsAttention.Row = NeedsAttentionRow;
